import { Link } from "@tanstack/react-router";
import { Package, TriangleAlertIcon, Truck } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { InTransitSummaryDTO } from "@/modules/inventory/domain/types";

type InTransitSummaryCardsProps = {
  summary?: InTransitSummaryDTO;
  reorderCount?: number;
  isLoading?: boolean;
};

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accentClass,
  href,
}: {
  icon: typeof Truck;
  label: string;
  value: number;
  hint?: string;
  accentClass: string;
  href?: string;
}) {
  const content = (
    <div className="brand-card flex items-center gap-3 p-4 transition-colors hover:bg-muted/30">
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${accentClass}`}
      >
        <Icon />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="font-display text-2xl tabular-nums leading-none">
          {value}
        </span>
        {hint ? (
          <span className="truncate text-muted-foreground text-xs">{hint}</span>
        ) : null}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {content}
      </Link>
    );
  }

  return content;
}

export function InTransitSummaryCards({
  summary,
  reorderCount = 0,
  isLoading,
}: InTransitSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {["a", "b"].map((id) => (
          <Skeleton key={id} className="h-[4.5rem] rounded-2xl" />
        ))}
      </div>
    );
  }

  const pendingHint =
    summary && summary.totalPendingQuantity > 0
      ? `${summary.totalPendingQuantity} ຊິ້ນລໍຖ້າຮັບ`
      : undefined;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <StatCard
        icon={Truck}
        label="ລໍຖ້າຮັບເຂົ້າ"
        value={summary?.atCarrierOrderCount ?? 0}
        hint={pendingHint}
        accentClass="bg-primary/20 text-secondary"
        href="/app/import-orders"
      />
      <StatCard
        icon={TriangleAlertIcon}
        label="ຄວນສັ່ງເພີ່ມ"
        value={reorderCount}
        hint="ສະຕັອກໃກ້ໝົດ · ບໍ່ມີຂອງລໍຖ້າ"
        accentClass="bg-destructive/15 text-destructive"
        href="/app/products"
      />
    </div>
  );
}

export function InTransitSummaryCardsSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {["a", "b"].map((id) => (
        <Skeleton key={id} className="h-[4.5rem] rounded-2xl" />
      ))}
    </div>
  );
}

/** ໄອຄອນ fallback ເມື່ອບໍ່ມີຮູບ */
export function PlanningListIcon({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
      {children}
    </div>
  );
}

export function PlanningPackageIcon() {
  return (
    <PlanningListIcon>
      <Package className="size-5" />
    </PlanningListIcon>
  );
}
